/**
 * Chutes Discipline Configuration
 * 
 * This configuration defines all aspects of the Chutes matrix:
 * - Column definitions
 * - Field mappings (frontend <-> backend)
 * - API endpoints
 * - Permissions
 * - Available features
 * 
 * Note: Chutes is a mechanical sub-table. It has its own:
 * - Template
 * - Rules of Credit table (rules_of_credit_chutes)
 * - Column configuration
 * 
 * Progress step columns will be added later per stakeholder specs.
 */

/**
 * Field mapping from frontend camelCase to backend snake_case
 */
export const FIELD_MAP = {
  buildingNumber: 'building_number',
  sovCode: 'sov_code',
  wbsCode: 'wbs_code',
  area: 'area',
  subArea: 'sub_area',
  elevation: 'elevation',
  floor: 'floor',
  equipmentTagOrCode: 'equipment_tag_or_code',
  description: 'description',
  equipmentType: 'equipment_type',
  equipment_type: 'equipment_type',
  budgetedHr: 'budgeted_hr',
  credit: 'credit',
  costCode: 'cost_code',
  dateEquipmentReceived: 'date_equipment_received',
  // Progress step columns
  receive: 'receive',
  installSupports: 'install_supports',
  fieldTrim: 'field_trim',
  weldBoltUp: 'weld_bolt_up',
  punchList: 'punch_list',
  drawingNumber: 'drawing_number',
  pAndId: 'p_and_id',
  divisionArea: 'division_area',
  subDivisionArea: 'sub_division_area',
  activityId: 'activity_id',
  activityName: 'activity_name',
  comments: 'comments',
  udf01: 'udf01',
  udf02: 'udf02',
  udf03: 'udf03',
  udf04: 'udf04',
  udf05: 'udf05',
  udf06: 'udf06',
  udf07: 'udf07',
  udf08: 'udf08',
  udf09: 'udf09',
  udf10: 'udf10',
};

/**
 * Columns that affect the total_field_completion calculation
 */
export const FIELD_COMPLETION_COLUMNS = [
  'receive',
  'installSupports',
  'fieldTrim',
  'weldBoltUp',
  'punchList',
];

/**
 * Editable columns for progress tracking
 * Note: Location/identification columns (drawingNumber, area, subArea, elevation, floor,
 * equipmentTagOrCode, description) are READ ONLY - they are set via import only.
 */
export const EDITABLE_COLUMNS = [
  'activityId', 'activityName',
  'buildingNumber', 'sovCode', 'wbsCode', 'budgetedHr', 'credit', 'costCode',
  'dateEquipmentReceived', 'equipmentType',
  'receive', 'installSupports', 'fieldTrim', 'weldBoltUp', 'punchList',
  'pAndId', 'divisionArea', 'subDivisionArea',
  'comments', 'udf01', 'udf02', 'udf03', 'udf04', 'udf05',
  'udf06', 'udf07', 'udf08', 'udf09', 'udf10',
];

/**
 * Transform raw API data to frontend format
 */
export const transformMatrixData = (matrixData) => {
  return matrixData.map((row, index) => {
    const transformed = {
      id: row.id || index,
      area: row.area || '',
      subArea: row.sub_area || '',
      buildingNumber: row.building_number || '',
      sovCode: row.sov_code || '',
      sovDescription: row.sov_description || '',
      wbsCode: row.wbs_code || '',
      floor: row.floor || '',
      divisionArea: row.division_area || '',
      subDivisionArea: row.sub_division_area || '',
      jobNumber: row.job_number || '',
      activityId: row.activity_id || '',
      activityName: row.activity_name || '',
      costCode: row.cost_code || '',
      elevation: row.elevation || '',
      pAndId: row.p_and_id || '',
      drawingNumber: row.drawing_number || '',
      equipmentTagOrCode: row.equipment_tag_or_code || '',
      description: row.description || '',
      equipmentType: row.equipment_type || '',
      dateEquipmentReceived: row.date_equipment_received || '',
      // Progress step columns
      receive: row.receive ? Math.round(row.receive) : 0,
      installSupports: row.install_supports ? Math.round(row.install_supports) : 0,
      fieldTrim: row.field_trim ? Math.round(row.field_trim) : 0,
      weldBoltUp: row.weld_bolt_up ? Math.round(row.weld_bolt_up) : 0,
      punchList: row.punch_list ? Math.round(row.punch_list) : 0,
      totalFieldCompletion: row.total_field_completion ? Math.round(row.total_field_completion) : 0,
      budgetedHr: row.budgeted_hr ? Number(row.budgeted_hr) : 0,
      credit: row.credit ? Number(row.credit) : 0,
      earnedHr: row.earned_hr ? Number(row.earned_hr) : 0,
      earnedCredit: row.earned_credit ? Number(row.earned_credit) : 0,
      createdAt: row.created_at || '',
      updatedAt: row.updated_at || '',
      comments: row.comments || '',
    };
    
    // Add UDF columns
    for (let i = 1; i <= 10; i++) {
      const udfKey = `udf${String(i).padStart(2, '0')}`;
      transformed[udfKey] = row[udfKey] || '';
    }
    
    return transformed;
  });
};

/**
 * Build change payload for saving a cell change
 */
export const buildChangePayload = (rowId, columnId, oldValue, newValue) => {
  const backendField = FIELD_MAP[columnId];
  if (!backendField) return null;
  return { id: rowId, changes: { [backendField]: newValue } };
};

/**
 * Chutes discipline configuration
 */
export const chutesConfig = {
  id: 'chutes',
  name: 'Chutes',
  menuId: 'mechanical-chutes',

  // Pin the selection checkbox + Equipment Tag + Description columns on horizontal scroll
  frozenColumnIds: ['equipmentTagOrCode', 'description'],
  freezeActionColumns: true,
  tableName: 'chutes_tracking',
  historyTableName: 'chutes_tracking_history',
  
  // API endpoints - use dedicated chutes routes under /mechanical/chutes
  api: {
    getData: '/mechanical/chutes/project/{projectId}',
    saveChanges: '/mechanical/chutes/save-changes',
    export: '/mechanical/chutes/export/{projectId}',
    upload: '/mechanical/chutes/upload-parsed',
    template: '/mechanical/chutes/template/{projectId}',
    priorities: '/priorities',
    columnMappings: '/mechanical/chutes/project/{projectId}/column-mappings',
    progressHistory: '/mechanical/chutes/{matrixId}/progress-history',
  },
  
  // Permission requirements (pt_* = Progress Tracking module)
  permissions: {
    edit: 'pt_edit',
    analytics: 'pt_analytics',
    import: 'pt_import',
    export: 'pt_export',
    template: 'pt_template',
    columnConfig: 'pt_columnconfig',
    roc: 'pt_roc',
    sfRatio: 'pt_sfratio',
    matBore: 'pt_matbore',
    delete: 'pt_delete',
  },
  
  // Column definitions
  // showInDashboard: true = visible in matrix UI
  // showInDashboard: false = hidden from UI but available for import/export
  columns: [
    // === VISIBLE COLUMNS ===
    // Drawing reference (first column) - READ ONLY (set via import)
    { id: 'drawingNumber', header: 'Drawing Number', accessor: 'drawingNumber', dbField: 'drawing_number', width: 100, editable: false, filterable: true, type: 'text', showInDashboard: true },
    // Location columns - READ ONLY (set via import)
    { id: 'area', header: 'Area', accessor: 'area', dbField: 'area', width: 80, editable: false, filterable: true, type: 'text', showInDashboard: true },
    { id: 'subArea', header: 'Sub Area', accessor: 'subArea', dbField: 'sub_area', width: 80, editable: false, filterable: true, type: 'text', showInDashboard: true },
    { id: 'elevation', header: 'Elevation', accessor: 'elevation', dbField: 'elevation', width: 70, editable: false, filterable: true, type: 'text', showInDashboard: true },
    { id: 'floor', header: 'Floor', accessor: 'floor', dbField: 'floor', width: 60, editable: false, filterable: true, type: 'text', showInDashboard: true },
    // Equipment identification - READ ONLY (set via import)
    { id: 'equipmentTagOrCode', header: 'Equipment Tag/Code', accessor: 'equipmentTagOrCode', dbField: 'equipment_tag_or_code', width: 120, editable: false, filterable: true, type: 'text', showInDashboard: true },
    { id: 'description', header: 'Description', accessor: 'description', dbField: 'description', width: 150, editable: false, filterable: true, type: 'text', showInDashboard: true },
    // Receipt tracking
    { id: 'dateEquipmentReceived', header: 'Date Equipment Received', accessor: 'dateEquipmentReceived', dbField: 'date_equipment_received', width: 110, editable: true, filterable: true, type: 'date', showInDashboard: true },
    // Progress step columns
    { id: 'receive', header: 'Receive', accessor: 'receive', dbField: 'receive', width: 80, editable: true, filterable: true, type: 'progress', options: [0, 25, 50, 75, 100], affectsCalculation: 'totalFieldCompletion', headerColor: '#E8F5E9', headerGroup: 'Field Progress', showInDashboard: true },
    { id: 'installSupports', header: 'Install Supports', accessor: 'installSupports', dbField: 'install_supports', width: 95, editable: true, filterable: true, type: 'progress', options: [0, 25, 50, 75, 100], affectsCalculation: 'totalFieldCompletion', headerColor: '#E8F5E9', headerGroup: 'Field Progress', showInDashboard: true },
    { id: 'fieldTrim', header: 'Field Trim', accessor: 'fieldTrim', dbField: 'field_trim', width: 80, editable: true, filterable: true, type: 'progress', options: [0, 25, 50, 75, 100], affectsCalculation: 'totalFieldCompletion', headerColor: '#E8F5E9', headerGroup: 'Field Progress', showInDashboard: true },
    { id: 'weldBoltUp', header: 'Weld/Bolt Up', accessor: 'weldBoltUp', dbField: 'weld_bolt_up', width: 85, editable: true, filterable: true, type: 'progress', options: [0, 25, 50, 75, 100], affectsCalculation: 'totalFieldCompletion', headerColor: '#E8F5E9', headerGroup: 'Field Progress', showInDashboard: true },
    { id: 'punchList', header: 'Punch List', accessor: 'punchList', dbField: 'punch_list', width: 80, editable: true, filterable: true, type: 'progress', options: [0, 25, 50, 75, 100], affectsCalculation: 'totalFieldCompletion', headerColor: '#E8F5E9', headerGroup: 'Field Progress', showInDashboard: true },
    // Total completion and comments
    { id: 'totalFieldCompletion', header: 'Total Completion', accessor: 'totalFieldCompletion', dbField: 'total_field_completion', width: 90, editable: false, filterable: true, type: 'progress-readonly', computed: true, headerColor: '#C8E6C9', headerGroup: 'Field Progress', showInDashboard: true },
    { id: 'comments', header: 'Comments', accessor: 'comments', dbField: 'comments', width: 150, editable: true, filterable: true, type: 'text', showInDashboard: true },
    
    // === HIDDEN COLUMNS (available for import/export and column config) ===
    { id: 'equipmentType', header: 'Type', accessor: 'equipmentType', dbField: 'equipment_type', width: 120, editable: true, filterable: true, type: 'text', showInDashboard: false },
    { id: 'id', header: 'ID', accessor: 'id', dbField: 'id', width: 50, editable: false, filterable: true, type: 'number', showInDashboard: false },
    { id: 'buildingNumber', header: 'Building Number', accessor: 'buildingNumber', dbField: 'building_number', width: 80, editable: true, filterable: true, type: 'text', showInDashboard: false },
    { id: 'sovCode', header: 'SOV Code', accessor: 'sovCode', dbField: 'sov_code', width: 80, editable: true, filterable: true, type: 'text', showInDashboard: false },
    { id: 'wbsCode', header: 'WBS Code', accessor: 'wbsCode', dbField: 'wbs_code', width: 80, editable: true, filterable: true, type: 'text', showInDashboard: false },
    { id: 'divisionArea', header: 'Division Area', accessor: 'divisionArea', dbField: 'division_area', width: 80, editable: true, filterable: true, type: 'text', showInDashboard: false },
    { id: 'subDivisionArea', header: 'Sub Division Area', accessor: 'subDivisionArea', dbField: 'sub_division_area', width: 80, editable: true, filterable: true, type: 'text', showInDashboard: false },
    { id: 'jobNumber', header: 'Job Number', accessor: 'jobNumber', dbField: 'job_number', width: 80, editable: false, filterable: true, type: 'text', showInDashboard: false },
    { id: 'activityId', header: 'Activity ID', accessor: 'activityId', dbField: 'activity_id', width: 130, editable: true, filterable: true, type: 'text', showInDashboard: true, headerGroup: 'Schedule' },
    { id: 'activityName', header: 'Activity Name', accessor: 'activityName', dbField: 'activity_name', width: 160, editable: true, filterable: true, type: 'text', showInDashboard: false, headerGroup: 'Schedule' },
    { id: 'budgetedHr', header: 'Budgeted Hr', accessor: 'budgetedHr', dbField: 'budgeted_hr', width: 80, editable: true, filterable: true, type: 'number', showInDashboard: false },
    { id: 'credit', header: 'Credit', accessor: 'credit', dbField: 'credit', width: 70, editable: true, filterable: true, type: 'number', showInDashboard: false },
    { id: 'earnedHr', header: 'Earned Hr', accessor: 'earnedHr', dbField: 'earned_hr', width: 80, editable: false, filterable: true, type: 'number', computed: true, showInDashboard: false },
    { id: 'earnedCredit', header: 'Earned Credit', accessor: 'earnedCredit', dbField: 'earned_credit', width: 90, editable: false, filterable: true, type: 'number', computed: true, showInDashboard: false },
    { id: 'costCode', header: 'Cost Code', accessor: 'costCode', dbField: 'cost_code', width: 80, editable: true, filterable: true, type: 'text', showInDashboard: false },
    { id: 'pAndId', header: 'P&ID', accessor: 'pAndId', dbField: 'p_and_id', width: 70, editable: true, filterable: true, type: 'text', showInDashboard: false },
    { id: 'createdAt', header: 'Created At', accessor: 'createdAt', dbField: 'created_at', width: 130, editable: false, filterable: true, type: 'datetime', showInDashboard: false },
    { id: 'updatedAt', header: 'Updated At', accessor: 'updatedAt', dbField: 'updated_at', width: 130, editable: false, filterable: true, type: 'datetime', showInDashboard: false },
  ],
  
  // UDF (User Defined Fields) columns - hidden by default, added dynamically based on project config
  udfColumns: Array.from({ length: 10 }, (_, i) => ({
    id: `udf${String(i + 1).padStart(2, '0')}`,
    header: `UDF ${String(i + 1).padStart(2, '0')}`,
    accessor: `udf${String(i + 1).padStart(2, '0')}`,
    dbField: `udf${String(i + 1).padStart(2, '0')}`,
    width: 100,
    editable: true,
    filterable: true,
    type: 'text',
    isUdf: true,
    showInDashboard: false,
  })),
  
  // Field mappings
  fieldMap: FIELD_MAP,
  editableColumns: EDITABLE_COLUMNS,
  
  // Data transformation
  transformData: transformMatrixData,
  buildChangePayload: buildChangePayload,
  
  // Available features for this discipline
  features: {
    dragFill: true,
    progressTrends: true,
    matBore: false,              // Not applicable to chutes
    rulesOfCredit: true,
    columnConfig: true,
    actualSpentUpload: false,    // Skip for now
    export: true,
    upload: true,
    presence: true,
    budgetReport: false,         // Not yet implemented
    earnedReport: false,         // Not yet implemented
    evmReport: false,            // Not yet implemented
  },
};

export default chutesConfig;
